import React, { useState, useEffect, useRef, useCallback } from 'react';
import Hls from 'hls.js';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faTrash, faVolumeHigh, faVolumeXmark, faTriangleExclamation, faEye } from '@fortawesome/free-solid-svg-icons';
import DeleteConfirmModal from './DeleteConfirmModal';
import './ProviderStories.css';

const ProviderStories = ({ provider, onClose, isOwner = false, onDeleteReel }) => {
    const [reels, setReels] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [progress, setProgress] = useState(null);
    const [loading, setLoading] = useState(true);
    const [videoError, setVideoError] = useState(false);
    const [isMuted, setIsMuted] = useState(true);
    const [retryCount, setRetryCount] = useState(0);
    const [isRetrying, setIsRetrying] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [reelToDelete, setReelToDelete] = useState(null);

    // Debug removido - funcionando correctamente
    const videoRef = useRef(null);
    const loadTimeoutRef = useRef(null);
    const hlsRef = useRef(null);
    const viewedReelsRef = useRef(new Set());

    // Verificar conectividad
    const checkConnectivity = () => {
        if (!navigator.onLine) {
            console.warn('Sin conexión a internet');
            return false;
        }
        return true;
    };

    // Función de reintento automático para errores de buffering
    const handleAutoRetry = useCallback((errorType, videoUrl) => {
        const maxRetries = 3;
        
        if (retryCount < maxRetries && checkConnectivity()) {
            setRetryCount(prev => prev + 1);
            
            // Mostrar indicador de retry en lugar de error
            setVideoError(false);
            setIsRetrying(true);
            
            setTimeout(() => {
                if (videoRef.current && videoUrl && hlsRef.current) {
                    try {
                        // Para HLS, reiniciar la conexión completamente
                        if (hlsRef.current && !hlsRef.current.destroyed) {
                            hlsRef.current.stopLoad();
                            setTimeout(() => {
                                if (hlsRef.current && !hlsRef.current.destroyed) {
                                    hlsRef.current.startLoad(-1); // Empezar desde el inicio
                                    
                                    // Intentar reproducir después de un delay
                                    setTimeout(() => {
                                        if (videoRef.current) {
                                            videoRef.current.play().then(() => {
                                                setIsRetrying(false);
                                            }).catch(err => {
                                                // Ignorar AbortError durante reintentos
                                                if (err.name === 'AbortError') {
                                                    return;
                                                }
                                                setIsRetrying(false);
                                            });
                                        }
                                    }, 500);
                                }
                            }, 200);
                        }
                    } catch (err) {
                        setIsRetrying(false);
                    }
                }
            }, 1500);
        } else if (retryCount >= maxRetries) {
            setIsRetrying(false);
            setVideoError(true);
        }
    }, [retryCount]);

    // Configurar reproductor de video (HLS o nativo)
    const setupVideo = useCallback((videoElement, videoUrl) => {
        if (!videoElement || !videoUrl) return;

        // Limpiar HLS anterior si existe
        if (hlsRef.current) {
            hlsRef.current.destroy();
            hlsRef.current = null;
        }

        // Verificar si es un archivo HLS (.m3u8)
        const isHLS = videoUrl.includes('.m3u8');

        if (isHLS && Hls.isSupported()) {
            // Usar HLS.js para archivos .m3u8
            const hls = new Hls({
                enableWorker: false,
                lowLatencyMode: true,
                backBufferLength: 90
            });

            hls.loadSource(videoUrl);
            hls.attachMedia(videoElement);

            // Event listener para cuando el video comience a reproducirse correctamente
            videoElement.addEventListener('playing', () => {
                setRetryCount(0);
                setVideoError(false);
            });

            hls.on(Hls.Events.MANIFEST_PARSED, () => {
                videoElement.play().catch(error => {
                    // Ignorar AbortError - es normal durante reintentos
                    if (error.name === 'AbortError') {
                        return;
                    }
                    setVideoError(true);
                });
            });

            hls.on(Hls.Events.ERROR, (event, data) => {
                // Manejo inteligente de errores comunes
                if (data.fatal) {
                    switch (data.type) {
                        case Hls.ErrorTypes.NETWORK_ERROR:
                            handleAutoRetry('network-error', videoUrl);
                            break;
                        case Hls.ErrorTypes.MEDIA_ERROR:
                            hls.recoverMediaError();
                            break;
                        default:
                            setVideoError(true);
                            break;
                    }
                } else {
                    // Errores no fatales - manejo automático más inteligente
                    switch (data.details) {
                        case 'bufferStalledError':
                        case 'bufferSeekOverHole':
                            // Estos son los errores que mencionas - intentar recuperación automática
                            // Primer intento: let HLS handle it automatically
                            setTimeout(() => {
                                if (videoRef.current && videoRef.current.paused && !videoRef.current.ended) {
                                    videoRef.current.play().catch(err => {
                                        // Ignorar AbortError durante auto-recovery
                                        if (err.name === 'AbortError') {
                                            return;
                                        }
                                        handleAutoRetry(data.details, videoUrl);
                                    });
                                }
                            }, 2000);
                            break;
                        case 'bufferFullError':
                            // Buffer lleno - generalmente se resuelve solo
                            break;
                        case 'manifestLoadError':
                        case 'fragLoadError':
                            // Errores de carga - reintentar carga automáticamente
                            setTimeout(() => {
                                if (hls && !hls.destroyed) {
                                    hls.startLoad();
                                }
                            }, 1000);
                            break;
                        default:
                            // Error no fatal manejado silenciosamente
                            break;
                    }
                }
            });

            hlsRef.current = hls;
        } else if (isHLS && videoElement.canPlayType('application/vnd.apple.mpegurl')) {
            // Safari nativo soporta HLS
            videoElement.src = videoUrl;
            
            // Event listener para cuando el video comience a reproducirse correctamente
            videoElement.addEventListener('playing', () => {
                setRetryCount(0);
                setVideoError(false);
            });
            
            videoElement.addEventListener('loadedmetadata', () => {
                videoElement.play().catch(error => {
                    console.error('Error playing native HLS:', error);
                    setVideoError(true);
                });
            });
        } else if (!isHLS) {
            // Video normal (mp4, webm, etc.)
            videoElement.src = videoUrl;
            
            // Event listener para cuando el video comience a reproducirse correctamente
            videoElement.addEventListener('playing', () => {
                setRetryCount(0);
                setVideoError(false);
            });
        } else {
            // HLS no soportado
            console.error('HLS no soportado en este navegador');
            setVideoError(true);
        }
    }, [handleAutoRetry]);

    const loadProviderReels = useCallback(async () => {
        try {
            const response = await fetch(`https://api.minymol.com/reels/provider/${provider.provider.id}?limit=10`);

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            // Validar que los datos sean válidos
            if (!Array.isArray(data)) {
                console.error('Los datos recibidos no son un array:', data);
                setReels([]);
                return;
            }

            // Filtrar reels que tengan videoUrl válida
            const validReels = data.filter(reel =>
                reel &&
                reel.videoUrl &&
                typeof reel.videoUrl === 'string' &&
                reel.videoUrl.trim() !== ''
            );

            setReels(validReels);
        } catch (error) {
            setReels([]);
        } finally {
            setLoading(false);
        }
    }, [provider.provider.id]);

    const updateProgress = useCallback(async (reelId) => {
        try {
            // Throttling: solo una llamada cada 2 segundos por reel
            const lastCall = updateProgress._lastCalls?.[reelId] || 0;
            const now = Date.now();
            if (now - lastCall < 2000) {
                return; // Skip si muy reciente
            }
            
            if (!updateProgress._lastCalls) updateProgress._lastCalls = {};
            updateProgress._lastCalls[reelId] = now;
            
            const response = await fetch(`https://api.minymol.com/reels/provider/${provider.provider.id}/progress/${reelId}`);
            if (response.ok) {
                const data = await response.json();
                setProgress(data);
            }
        } catch (error) {
            // Silenciar errores de rate limiting
            if (!error.message?.includes('429')) {
                console.error('Error updating progress:', error);
            }
        }
    }, [provider.provider.id]);

    const incrementView = useCallback(async (reelId) => {
        // Evitar múltiples incrementos para el mismo reel
        if (viewedReelsRef.current.has(reelId)) {
            return;
        }
        
        viewedReelsRef.current.add(reelId);
        try {
            const response = await fetch(`https://api.minymol.com/reels/${reelId}/view`, {
                method: 'POST'
            });
            // Solo log si no es rate limiting
            if (!response.ok && response.status !== 429) {
                throw new Error(`HTTP ${response.status}`);
            }
        } catch (error) {
            // Silenciar errores de rate limiting y solo log otros errores
            if (!error.message?.includes('429')) {
                console.error('Error incrementing view:', error);
            }
            // Si hay error, remover del Set para permitir reintento
            viewedReelsRef.current.delete(reelId);
        }
    }, []);

    const preloadNext = useCallback(async (reelId) => {
        try {
            // Throttling: solo una precarga cada 3 segundos
            const lastPreload = preloadNext._lastCall || 0;
            const now = Date.now();
            if (now - lastPreload < 3000) {
                return; // Skip si muy reciente
            }
            preloadNext._lastCall = now;
            
            const response = await fetch(`https://api.minymol.com/reels/preload/next/${reelId}?count=2`);
            if (response.ok) {
                const data = await response.json();
                // Precargar videos en background
                data.preloadUrls?.forEach(preloadVideo);
            }
        } catch (error) {
            // Silenciar errores de rate limiting
            if (!error.message?.includes('429')) {
                console.error('Error preloading:', error);
            }
        }
    }, []);

    useEffect(() => {
        loadProviderReels();

        // Cleanup timeout and HLS on unmount
        return () => {
            if (loadTimeoutRef.current) {
                clearTimeout(loadTimeoutRef.current);
            }
            if (hlsRef.current) {
                hlsRef.current.destroy();
                hlsRef.current = null;
            }
        };
    }, [loadProviderReels]);

    useEffect(() => {
        if (reels.length > 0 && currentIndex < reels.length) {
            const currentReel = reels[currentIndex];
            updateProgress(currentReel.id);
            incrementView(currentReel.id);
            preloadNext(currentReel.id);

            // Resetear estados al cambiar de historia
            setRetryCount(0);
            setVideoError(false);
            setIsRetrying(false);

            // Configurar el video cuando cambie el índice
            if (videoRef.current && currentReel.videoUrl) {
                setupVideo(videoRef.current, currentReel.videoUrl);
            }
        }
    }, [currentIndex, reels, updateProgress, incrementView, preloadNext, setupVideo]);

    const preloadVideo = (videoData) => {
        const video = document.createElement('video');
        video.preload = 'metadata';
        video.src = videoData.videoUrl;
    };

    const nextReel = () => {
        setVideoError(false);
        if (currentIndex < reels.length - 1) {
            setCurrentIndex(currentIndex + 1);
        } else {
            onClose();
        }
    };

    const prevReel = () => {
        setVideoError(false);
        if (currentIndex > 0) {
            setCurrentIndex(currentIndex - 1);
        }
    };

    const toggleMute = () => {
        setIsMuted(!isMuted);
        if (videoRef.current) {
            videoRef.current.muted = !isMuted;
        }
    };

    const confirmDeleteReel = (reel) => {
        setReelToDelete(reel);
        setShowDeleteModal(true);
    };

    const handleDeleteReel = async () => {
        if (reelToDelete && onDeleteReel) {
            await onDeleteReel(reelToDelete);
            setShowDeleteModal(false);
            setReelToDelete(null);
            // Cerrar el modal de stories después de eliminar
            onClose();
        }
    };

    const cancelDeleteReel = () => {
        setShowDeleteModal(false);
        setReelToDelete(null);
    };

    const handleVideoEnd = () => {
        nextReel();
    };

    if (loading) {
        return (
            <div className="reels-stories-modal">
                <div className="stories-loading">
                    <div className="loading-spinner"></div>
                </div>
            </div>
        );
    }

    if (reels.length === 0) {
        return (
            <div className="reels-stories-modal">
                <div className="stories-empty">
                    <p>No hay historias disponibles</p>
                    <button onClick={onClose}>Cerrar</button>
                </div>
            </div>
        );
    }

    const currentReel = reels[currentIndex];

    // Validar que el reel actual tenga una URL de video válida
    if (!currentReel || !currentReel.videoUrl) {
        console.error('Reel inválido o sin videoUrl:', currentReel);
        return (
            <div className="reels-stories-modal">
                <div className="stories-empty">
                    <p>Error al cargar el video</p>
                    <button onClick={onClose}>Cerrar</button>
                </div>
            </div>
        );
    }

    return (
        <div className="reels-stories-modal" onClick={onClose}>
            <div className="reels-stories-content" onClick={(e) => e.stopPropagation()}>
                {/* Barras de progreso */}
                <div className="progress-bars">
                    {reels.map((_, i) => (
                        <div
                            key={i}
                            className={`progress-bar ${i < currentIndex ? 'completed' : i === currentIndex ? 'current' : 'pending'}`}
                        />
                    ))}
                </div>

                {/* Header */}
                <div className="stories-header">
                    <div className="provider-info">
                        <img
                            src={provider.provider.logo_url || '/logo.webp'}
                            alt="Logo"
                            className="provider-avatar"
                            onError={(e) => {
                                e.target.src = '/logo.webp';
                            }}
                        />
                        <div className="provider-details">
                            <span className="provider-name">{provider.provider.nombre_empresa}</span>
                            <span className="story-time">{progress?.currentIndex || 1}/{progress?.totalReels || reels.length}</span>
                        </div>
                    </div>
                    <div className="reels-header-actions">
                        {isOwner && (
                            <button
                                className="reels-delete-btn"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    confirmDeleteReel(currentReel);
                                }}
                                title="Eliminar historia"
                            >
                                <FontAwesomeIcon icon={faTrash} />
                            </button>
                        )}
                        <button className="close-btn" onClick={onClose}>
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                    </div>
                </div>

                {/* Video */}
                <div className="video-container">
                    {isRetrying ? (
                        <div className="retry-indicator">
                            <div className="retry-content">
                                <div className="retry-spinner"></div>
                                <h3>Reintentando conexión...</h3>
                                <p>Recuperando video automáticamente</p>
                            </div>
                        </div>
                    ) : videoError ? (
                        <div className="video-error">
                            <div className="error-content">
                                <div className="error-icon">
                                    <FontAwesomeIcon icon={faTriangleExclamation} />
                                </div>
                                <h3>Error al cargar el video</h3>
                                <p>
                                    {!navigator.onLine
                                        ? 'Sin conexión a internet. Verifica tu conectividad.'
                                        : 'No se pudo reproducir este contenido. Puede ser un problema temporal del servidor o el video no está disponible.'
                                    }
                                </p>
                                <div className="error-actions">
                                    <button onClick={() => {
                                        if (!checkConnectivity()) {
                                            alert('Verifica tu conexión a internet');
                                            return;
                                        }
                                        setVideoError(false);
                                        setRetryCount(0); // Resetear contador al reintentar manualmente
                                        setIsRetrying(false);
                                        if (videoRef.current && currentReel.videoUrl) {
                                            setupVideo(videoRef.current, currentReel.videoUrl);
                                        }
                                    }}>
                                        Reintentar
                                    </button>
                                    <button onClick={nextReel}>
                                        Siguiente
                                    </button>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <video
                            ref={videoRef}
                            muted={isMuted}
                            playsInline
                            onEnded={handleVideoEnd}
                            onError={(e) => {
                                console.error('Video error:', {
                                    error: e,
                                    videoUrl: currentReel.videoUrl,
                                    videoId: currentReel.id,
                                    networkState: e.target.networkState,
                                    readyState: e.target.readyState,
                                    errorCode: e.target.error?.code,
                                    errorMessage: e.target.error?.message
                                });
                                setVideoError(true);
                            }}
                            onLoadStart={() => {
                                setVideoError(false);

                                // Timeout de 15 segundos para cargar el video HLS
                                if (loadTimeoutRef.current) {
                                    clearTimeout(loadTimeoutRef.current);
                                }
                                loadTimeoutRef.current = setTimeout(() => {
                                    setVideoError(true);
                                }, 15000);
                            }}
                            onCanPlay={() => {
                                // Limpiar timeout si el video se puede reproducir
                                if (loadTimeoutRef.current) {
                                    clearTimeout(loadTimeoutRef.current);
                                    loadTimeoutRef.current = null;
                                }
                            }}
                            onLoadedData={() => {
                                // Resetear estados cuando el video carga exitosamente
                                setRetryCount(0);
                                setVideoError(false);
                                setIsRetrying(false);
                            }}
                        />
                    )}

                    {/* Controles de navegación */}
                    <div className="video-controls">
                        <div className="control-area left" onClick={prevReel}></div>
                        <div className="control-area right" onClick={nextReel}></div>
                    </div>

                    {/* Indicador de audio silenciado */}
                    {isMuted && (
                        <div className="muted-indicator">
                            <FontAwesomeIcon icon={faVolumeXmark} />
                        </div>
                    )}

                    {/* Info del reel */}
                    <div className="reel-info">
                        <div className="reel-stats">
                            <span className="reel-duration">{currentReel.duration}s</span>
                            {isOwner && (
                                <span className="reel-views">
                                    <FontAwesomeIcon icon={faEye} style={{ color: 'white' }} /> {currentReel.views || 0}
                                </span>
                            )}
                        </div>
                        <button className="mute-btn" onClick={toggleMute}>
                            <FontAwesomeIcon icon={isMuted ? faVolumeXmark : faVolumeHigh} />
                        </button>
                    </div>
                </div>
            </div>

            {/* Modal de confirmación de eliminación */}
            {showDeleteModal && reelToDelete && (
                <DeleteConfirmModal
                    reel={reelToDelete}
                    onConfirm={handleDeleteReel}
                    onCancel={cancelDeleteReel}
                />
            )}
        </div>
    );
};

export default ProviderStories;
